/** A line of a diff: in both texts, only in the new one, or only in the old one; numbers from 1. */
export interface DiffLine {
  kind: 'same' | 'add' | 'del';
  text: string;
  /** Its number in the old text. */
  a?: number;
  /** Its number in the new text. */
  b?: number;
}

/** What a diff shows: changed lines with some lines around them, and how many unchanged lines are left out between. */
export type DiffPart = { kind: 'lines'; lines: DiffLine[] } | { kind: 'skip'; count: number };

/** Beyond this many cells of the comparison table a diff is too large for the browser. */
const MAX_CELLS = 4_000_000;

const linesOf = (text: string) => {
  if (!text) return [];
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  return lines;
};

/**
 * Line diff of two texts (longest common subsequence after the common start and end are taken off). null when the
 * changed middle is too large to compare.
 */
export function diffLines(before: string, after: string): DiffLine[] | null {
  const a = linesOf(before);
  const b = linesOf(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const n = endA - start;
  const m = endB - start;
  if ((n + 1) * (m + 1) > MAX_CELLS) return null;

  // lcs[i * (m + 1) + j]: length of the common subsequence of a[start + i..] and b[start + j..].
  const w = m + 1;
  const lcs = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * w + j] = a[start + i] === b[start + j] ? lcs[(i + 1) * w + j + 1] + 1 : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);
    }
  }

  const out: DiffLine[] = [];
  for (let k = 0; k < start; k++) out.push({ kind: 'same', text: a[k], a: k + 1, b: k + 1 });
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[start + i] === b[start + j]) {
      out.push({ kind: 'same', text: a[start + i], a: start + i + 1, b: start + j + 1 });
      i++;
      j++;
    } else if (i < n && (j >= m || lcs[(i + 1) * w + j] >= lcs[i * w + j + 1])) {
      // The old line first, as diffs show it.
      out.push({ kind: 'del', text: a[start + i], a: start + i + 1 });
      i++;
    } else {
      out.push({ kind: 'add', text: b[start + j], b: start + j + 1 });
      j++;
    }
  }
  for (let k = 0; k < a.length - endA; k++) out.push({ kind: 'same', text: a[endA + k], a: endA + k + 1, b: endB + k + 1 });
  return out;
}

/** The changed lines with `context` lines around each change; the unchanged stretches between are left out. */
export function diffParts(lines: DiffLine[], context = 3): DiffPart[] {
  const keep = new Array<boolean>(lines.length).fill(false);
  lines.forEach((line, k) => {
    if (line.kind === 'same') return;
    for (let x = Math.max(0, k - context); x <= Math.min(lines.length - 1, k + context); x++) keep[x] = true;
  });
  const parts: DiffPart[] = [];
  let k = 0;
  while (k < lines.length) {
    const from = k;
    const kept = keep[k];
    while (k < lines.length && keep[k] === kept) k++;
    parts.push(kept ? { kind: 'lines', lines: lines.slice(from, k) } : { kind: 'skip', count: k - from });
  }
  return parts;
}
